import "./CustomTicketModal.scss";
import { useState, useRef, useEffect } from "react";
import { Modal } from "react-bootstrap";
import FakeQrCode from "../../images/FakeQrCode.png";
import updateEventWitchCustomizeTicket from "../../utility/updateEventWitchCustomizeTicket";
import SuccessLottie from "../../components/lotties/success.json";
import Lottie from "lottie-react";
import { storage } from "../../firebase";
import { getDownloadURL, ref } from "firebase/storage";
import { useTranslation } from "react-i18next";

import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument } from "pdf-lib";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PDF_SIZE = { height: 842, width: 595, toleranceMargin: 10 };
const TEXT_HEIGHT = 40;
const TEXT_FONT = 10;
const TEXT_CONTENT = "aIgCE62m8XnbU1zMPCPv1";
const TEXT_CONTENT_TICKET = "VIP Ticket x2";

const CustomTicketModal = ({
  show,
  onClose,
  eventId,
  customTicketConfiguration,
  ticketId = null,
  ticketName = "",
}) => {
  const [file, setFile] = useState(null);
  const [pageSize, setPageSize] = useState({ width: 450, height: 600 });
  const [qrConfig, setQrConfig] = useState({
    x: customTicketConfiguration?.x || 0,
    y: customTicketConfiguration?.y || 0,
    size: customTicketConfiguration?.size || 0.35,
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation("ticketingDashboard");

  const pageRef = useRef(null);
  const inputRef = useRef(null);

  const PRINT_MARGIN = { horizontal: 0.03, vertical: 0.05 };
  const qrHeightRatio = (qrConfig.size * pageSize.width + TEXT_HEIGHT) / pageSize.height;
  const maxX = Math.max(0, 1 - qrConfig.size - PRINT_MARGIN.horizontal);
  const maxY = Math.max(0, 1 - qrHeightRatio - PRINT_MARGIN.vertical);

  useEffect(() => {
    if (!pageRef.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setPageSize({ width, height });
    });

    observer.observe(pageRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!show) {
      setSuccess(false);
      setFile(null);
    } else {
      const fetchPdf = async () => {
        if (customTicketConfiguration?.templatePath) {
          try {
            setError("");
            const url = await getDownloadURL(ref(storage, customTicketConfiguration.templatePath));
            setFile(url);
          } catch (err) {
            setError(t("downloadPdfError"));
            console.error("Erreur lors du téléchargement du PDF :", err);
            setFile(null);
          }
        }
      };

      fetchPdf();
      setQrConfig({
        x: customTicketConfiguration?.x ?? PRINT_MARGIN.horizontal,
        y: customTicketConfiguration?.y ?? PRINT_MARGIN.vertical,
        size: customTicketConfiguration?.size ?? 0.35,
      });
    }
  }, [show, customTicketConfiguration, ticketId]);

  async function checkPdfSise(file) {
    setError("");
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arrayBuffer);
    const firstPage = pdfDoc.getPage(0);

    if (
      Math.abs(firstPage.getWidth() - PDF_SIZE.width) > PDF_SIZE.toleranceMargin ||
      Math.abs(firstPage.getHeight() - PDF_SIZE.height) > PDF_SIZE.toleranceMargin
    ) {
      setError(t("invalidPdfFormatError"));
      return false;
    }

    return true;
  }

  async function extractFirstPage(file) {
    try {
      setError("");
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      const newPdf = await PDFDocument.create();
      const [firstPage] = await newPdf.copyPages(pdfDoc, [0]);
      newPdf.addPage(firstPage);

      const pdfBytes = await newPdf.save();
      return new Blob([pdfBytes], { type: "application/pdf" });
    } catch (error) {
      setError(t("extractPageError"));
      console.error("Erreur lors de l'extraction de la première page:", error);
    }
  }

  async function handleFileChange(event) {
    const selectedFile = event.target.files[0];
    if (selectedFile && selectedFile.type === "application/pdf") {
      try {
        setError("");

        const sizeValid = await checkPdfSise(selectedFile);

        if (sizeValid) {
          const firstPagePdf = await extractFirstPage(selectedFile);
          setFile(firstPagePdf);
        }
      } catch (error) {
        setError(t("loadPdfError"));
        console.error("Erreur lors du chargement du PDF : ", error);
      }
    }
  }

  async function handleSave() {
    try {
      setError("");
      await updateEventWitchCustomizeTicket({
        eventId,
        ticketId,
        customizeTicketsDetails: { ...qrConfig, pdf: file },
      });
      setSuccess(true);
      onClose();
    } catch (error) {
      setError(t("updatePdfError"));
      console.error("Erreur lors de la mise à jour du PDF : ", error);
    }
  }

  async function handleDelete() {
    try {
      setError("");
      await updateEventWitchCustomizeTicket({
        eventId,
        ticketId,
        customizeTicketsDetails: null,
      });
      setSuccess(true);
      onClose();
    } catch (error) {
      setError(t("deletePdfError"));
      console.error("Erreur lors de la suppression du PDF : ", error);
    }
  }

  function handleQrConfigChange(field, value) {
    setQrConfig((prev) => ({ ...prev, [field]: value }));
  }

  function handleSizeChange(newSize) {
    const qrWidth = newSize * pageSize.width;
    let newX = qrConfig.x;
    let newY = qrConfig.y;

    const leftSpace = newX - PRINT_MARGIN.horizontal;
    const rightSpace = 1 - (newX + newSize) - PRINT_MARGIN.horizontal;
    const topSpace = newY - PRINT_MARGIN.vertical;
    const bottomSpace =
      1 - newY - (qrWidth + TEXT_HEIGHT) / pageSize.height - PRINT_MARGIN.vertical;

    if (rightSpace < 0 && leftSpace > 0) {
      newX = Math.max(PRINT_MARGIN.horizontal, 1 - newSize - PRINT_MARGIN.horizontal);
    } else if (leftSpace < 0 && rightSpace > 0) {
      newX = PRINT_MARGIN.horizontal;
    }

    if (bottomSpace < 0 && topSpace > 0) {
      newY = Math.max(
        PRINT_MARGIN.vertical,
        1 - (qrWidth + TEXT_HEIGHT) / pageSize.height - PRINT_MARGIN.vertical,
      );
    } else if (topSpace < 0 && bottomSpace > 0) {
      newY = PRINT_MARGIN.vertical;
    }

    setQrConfig((prev) => ({
      ...prev,
      size: newSize,
      x: newX,
      y: newY,
    }));
  }

  function centerHorizontally() {
    const availableWidth = 1 - 2 * PRINT_MARGIN.horizontal - qrConfig.size;
    const centerX = PRINT_MARGIN.horizontal + availableWidth / 2;
    setQrConfig((prev) => ({ ...prev, x: centerX }));
  }

  function centerVertically() {
    const availableHeight = 1 - 2 * PRINT_MARGIN.vertical - qrHeightRatio;
    const centerY = PRINT_MARGIN.vertical + availableHeight / 2;
    setQrConfig((prev) => ({ ...prev, y: centerY }));
  }

  function centerOnPage() {
    centerHorizontally();
    centerVertically();
  }

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="custom-ticket-modal">
      <div className="custom-ticket-modal-header">
        {ticketId ? t("customizeSpecificTicket", { ticketName }) : t("customizeTicket")}
      </div>

      <div className="custom-ticket-modal-content">
        {success ? (
          <Lottie
            className="custom-ticket-modal-content-lottie"
            animationData={SuccessLottie}
            speed={0.6}
          />
        ) : (
          <>
            <input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              ref={inputRef}
              hidden
            />

            <div className="custom-ticket-modal-content-pdf">
              <div className="custom-ticket-modal-content-pdf-file">
                {file ? (
                  <>
                    <Document file={file} loading={<div className="pdf-skeleton" />}>
                      <Page
                        pageNumber={1}
                        width={450}
                        inputRef={pageRef}
                        onLoadSuccess={(page) =>
                          setPageSize({ width: page.width, height: page.height })
                        }
                      />
                    </Document>

                    <img
                      src={FakeQrCode}
                      alt="QR Code"
                      className="custom-ticket-modal-content-pdf-qrcode"
                      style={{
                        top: qrConfig.y * pageSize.height,
                        left: qrConfig.x * pageSize.width,
                        width: qrConfig.size * pageSize.width,
                        height: qrConfig.size * pageSize.width,
                      }}
                    />

                    <div
                      className="custom-ticket-modal-content-pdf-id"
                      style={{
                        top: qrConfig.y * pageSize.height + qrConfig.size * pageSize.width,
                        left: qrConfig.x * pageSize.width,
                        width: qrConfig.size * pageSize.width,
                        fontSize: TEXT_FONT,
                        height: TEXT_HEIGHT,
                        lineHeight: 1.2,
                        paddingTop: 4,
                      }}
                    >
                      <span>{TEXT_CONTENT}</span>
                      <span>{TEXT_CONTENT_TICKET}</span>
                    </div>
                  </>
                ) : (
                  <div className="custom-ticket-modal-content-pdf-file-empty">
                    <p>{t("noPdf")}</p>
                    <button
                      type="button"
                      className="custom-ticket-modal-content-pdf-upload-btn"
                      onClick={() => inputRef.current?.click()}
                    >
                      {t("choosePdf")}
                    </button>
                  </div>
                )}
              </div>

              <div className="custom-ticket-modal-content-pdf-controls">
                <label className="custom-ticket-modal-content-pdf-controls-title">
                  {t("centeringActions")}
                </label>

                <div className="custom-ticket-modal-content-pdf-controls-container">
                  <div className="custom-ticket-modal-content-pdf-controls-container-buttons">
                    <button className="center-btn" onClick={centerHorizontally} disabled={!file}>
                      {t("centerH")}
                    </button>
                    <button className="center-btn" onClick={centerVertically} disabled={!file}>
                      {t("centerV")}
                    </button>
                    <button className="center-btn" onClick={centerOnPage} disabled={!file}>
                      {t("centerPage")}
                    </button>
                  </div>

                  <div className="custom-ticket-modal-content-pdf-controls-container-range">
                    <div className="custom-ticket-modal-content-pdf-controls-container-range-group">
                      <label>
                        {t("xAxis")} {qrConfig.x.toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min={PRINT_MARGIN.horizontal}
                        max={maxX}
                        step={0.01}
                        value={qrConfig.x}
                        onChange={(e) => handleQrConfigChange("x", parseFloat(e.target.value))}
                        disabled={!file}
                      />
                    </div>

                    <div className="custom-ticket-modal-content-pdf-controls-container-range-group">
                      <label>
                        {t("yAxis")} {qrConfig.y.toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min={PRINT_MARGIN.vertical}
                        max={maxY}
                        step={0.01}
                        value={qrConfig.y}
                        onChange={(e) => handleQrConfigChange("y", parseFloat(e.target.value))}
                        disabled={!file}
                      />
                    </div>

                    <div className="custom-ticket-modal-content-pdf-controls-container-range-group">
                      <label>
                        {t("size")} {qrConfig.size.toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min={0.35}
                        max={0.8}
                        step={0.01}
                        value={qrConfig.size}
                        onChange={(e) => handleSizeChange(parseFloat(e.target.value))}
                        disabled={!file}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {error && <div className="custom-ticket-modal-error">{error}</div>}

      <div className="custom-ticket-modal-footer">
        {success ? (
          <button className="btn btn-cancel" onClick={onClose}>
            {t("close")}
          </button>
        ) : (
          <>
            <button className="btn btn-cancel" onClick={onClose}>
              {t("cancel")}
            </button>
            <button
              className="btn btn-edit"
              onClick={() => inputRef.current?.click()}
              disabled={!file}
            >
              {t("editImage")}
            </button>
            <button className="btn btn-delete" onClick={handleDelete} disabled={!file}>
              {t("delete")}
            </button>
            <button className="btn btn-save" onClick={handleSave} disabled={!file}>
              {t("save")}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CustomTicketModal;
